// ============================================================================
// OPTIMIZED EMPLOYEE IMAGE GALLERY - Performance Enhanced
// Path: frontend/src/components/dashboard/sections/EmployeeImageGallery.tsx
//
// ✨ Performance Optimizations:
// - Removed all console.log statements for better INP scores
// - Consistent grouping logic with useMemo
// - All render functions properly memoized
// - Lazy image loading
// - Optimized re-renders with React.memo
// ============================================================================

import React, { useMemo, useCallback } from 'react';
import { CurrentCheckIn } from '../../../services/locationattendance.service';
import { Employee, getDesignationInfo, isSpecialDesignation, SPECIAL_DESIGNATIONS } from '../../../services/ptsemployees.service';
import styles from './EmployeeImageGallery.module.css';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface EmployeeImageGalleryProps {
  employees: Array<{
    checkIn: CurrentCheckIn;
    employee: Employee | null;
  }>;
  projectName: string;
  onClose: () => void;
}

interface EmployeeWithCheckIn {
  checkIn: CurrentCheckIn;
  employee: Employee;
}

interface GroupedData {
  specialGroups: Record<string, EmployeeWithCheckIn[]>;
  generalEmployees: EmployeeWithCheckIn[];
  hasSpecialGroups: boolean;
  totalEmployees: number;
  groupingSummary: {
    specialGroupCount: number;
    totalInSpecialGroups: number;
    generalCount: number;
  };
}

// ============================================================================
// CONSTANTS - Moved outside component for performance
// ============================================================================

const GROUPING_CONFIG = {
  MIN_GROUP_SIZE: 4,
  MAX_VISIBLE_IN_GROUP: 6,
  MAX_VISIBLE_GENERAL: 6,
  MAX_VISIBLE_GENERAL_WITH_GROUPS: 4,
  DESIGNATION_PRIORITY: [
    'Site Engineer',
    'Site Civil Engineer', 
    'Construction Manager',
    'Civil Foreman',
    'Foreman (Agriculture)',
    'Foreman (Irrigation)',
    'Foreman (MEP Technician)',
    'Foreman ( Landscape)',
    'Softscape & Irrigation Foreman',
    'Mason'
  ] as const
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const sortEmployeesByPriority = (employees: EmployeeWithCheckIn[]): EmployeeWithCheckIn[] => {
  return [...employees].sort((a, b) => {
    const aDesignation = a.employee.designation || '';
    const bDesignation = b.employee.designation || '';
    
    const aPriority = GROUPING_CONFIG.DESIGNATION_PRIORITY.indexOf(aDesignation as any);
    const bPriority = GROUPING_CONFIG.DESIGNATION_PRIORITY.indexOf(bDesignation as any);
    
    if (aPriority === -1 && bPriority === -1) return aDesignation.localeCompare(bDesignation);
    if (aPriority === -1) return 1;
    if (bPriority === -1) return -1;
    return aPriority - bPriority;
  });
};

const groupEmployeesByDesignation = (
  sortedEmployees: EmployeeWithCheckIn[]
): { 
  designationGroups: Record<string, EmployeeWithCheckIn[]>; 
  generalEmployees: EmployeeWithCheckIn[] 
} => {
  const designationGroups: Record<string, EmployeeWithCheckIn[]> = {};
  const generalEmployees: EmployeeWithCheckIn[] = [];

  sortedEmployees.forEach(({ checkIn, employee }) => {
    const designation = employee.designation;
    
    if (designation && Object.keys(SPECIAL_DESIGNATIONS).includes(designation)) {
      if (!designationGroups[designation]) {
        designationGroups[designation] = [];
      }
      designationGroups[designation].push({ checkIn, employee });
    } else {
      generalEmployees.push({ checkIn, employee });
    }
  });

  return { designationGroups, generalEmployees };
};

const applyGroupingRules = (
  designationGroups: Record<string, EmployeeWithCheckIn[]>,
  generalEmployees: EmployeeWithCheckIn[]
): {
  specialGroups: Record<string, EmployeeWithCheckIn[]>;
  regroupedGeneral: EmployeeWithCheckIn[];
} => {
  const specialGroups: Record<string, EmployeeWithCheckIn[]> = {};
  let regroupedGeneral: EmployeeWithCheckIn[] = [...generalEmployees];

  Object.entries(designationGroups).forEach(([designation, employeeList]) => {
    if (employeeList.length >= GROUPING_CONFIG.MIN_GROUP_SIZE) {
      specialGroups[designation] = employeeList;
    } else {
      regroupedGeneral.push(...employeeList);
    }
  });

  return { specialGroups, regroupedGeneral };
};

const sortGroupsByPriority = (
  specialGroups: Record<string, EmployeeWithCheckIn[]>
): Record<string, EmployeeWithCheckIn[]> => {
  const sortedSpecialGroups: Record<string, EmployeeWithCheckIn[]> = {};
  
  GROUPING_CONFIG.DESIGNATION_PRIORITY.forEach(designation => {
    if (specialGroups[designation]) {
      sortedSpecialGroups[designation] = specialGroups[designation];
    }
  });

  Object.keys(specialGroups)
    .filter(designation => !GROUPING_CONFIG.DESIGNATION_PRIORITY.includes(designation as any))
    .sort()
    .forEach(designation => {
      sortedSpecialGroups[designation] = specialGroups[designation];
    });

  return sortedSpecialGroups;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const EmployeeImageGallery: React.FC<EmployeeImageGalleryProps> = ({
  employees,
  projectName,
  onClose
}) => {
  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  const validEmployees = useMemo(() => 
    employees.filter(item => item.employee !== null) as EmployeeWithCheckIn[],
    [employees]
  );

  const groupedData = useMemo((): GroupedData => {
    const sortedEmployees = sortEmployeesByPriority(validEmployees);
    const { designationGroups, generalEmployees } = groupEmployeesByDesignation(sortedEmployees);
    const { specialGroups, regroupedGeneral } = applyGroupingRules(designationGroups, generalEmployees);
    const sortedSpecialGroups = sortGroupsByPriority(specialGroups);

    return {
      specialGroups: sortedSpecialGroups,
      generalEmployees: regroupedGeneral,
      hasSpecialGroups: Object.keys(sortedSpecialGroups).length > 0,
      totalEmployees: validEmployees.length,
      groupingSummary: {
        specialGroupCount: Object.keys(sortedSpecialGroups).length,
        totalInSpecialGroups: Object.values(sortedSpecialGroups).reduce((sum, group) => sum + group.length, 0),
        generalCount: regroupedGeneral.length
      }
    };
  }, [validEmployees]);

  // ============================================================================
  // CALLBACK FUNCTIONS
  // ============================================================================

  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.style.display = 'none';
    const initialsDiv = target.nextElementSibling as HTMLDivElement;
    if (initialsDiv) {
      initialsDiv.style.display = 'flex';
    }
  }, []);

  const renderEmployeeImage = useCallback((
    employee: Employee, 
    index: number, 
    groupDesignation?: string
  ) => {
    const designationInfo = getDesignationInfo(employee.designation);
    const isSpecial = isSpecialDesignation(employee.designation);
    
    const effectiveDesignation = groupDesignation || employee.designation;
    const effectiveDesignationInfo = getDesignationInfo(effectiveDesignation);
    
    const initials = employee.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    
    return (
      <div 
        key={`${employee.id}-${index}-${effectiveDesignation}`} 
        className={`${styles.employeeImageContainer} ${isSpecial ? styles.specialDesignation : ''}`}
        title={`${employee.name}${employee.designation ? `\n${employee.designation}` : ''}`}
        style={{
          '--designation-color': effectiveDesignationInfo.color
        } as React.CSSProperties}
        data-designation={effectiveDesignation}
        data-is-special={isSpecial}
      >
        {employee.image && employee.image.trim() ? (
          <img 
            src={employee.image} 
            alt={employee.name}
            className={styles.employeeImage}
            loading="lazy"
            onError={handleImageError}
          />
        ) : null}
        <div 
          className={styles.employeeInitials}
          style={{ 
            display: employee.image && employee.image.trim() ? 'none' : 'flex',
            background: isSpecial ? effectiveDesignationInfo.color : undefined
          }}
        >
          {initials}
        </div>
        
        {isSpecial && (
          <div 
            className={styles.designationBadge}
            style={{ backgroundColor: effectiveDesignationInfo.color }}
          >
            {effectiveDesignationInfo.icon}
          </div>
        )}
      </div>
    );
  }, [handleImageError]);

  const renderDesignationGroup = useCallback((
    designation: string, 
    employeeList: EmployeeWithCheckIn[]
  ) => {
    const designationInfo = getDesignationInfo(designation);
    const visibleEmployees = employeeList.slice(0, GROUPING_CONFIG.MAX_VISIBLE_IN_GROUP);
    const remainingCount = employeeList.length - GROUPING_CONFIG.MAX_VISIBLE_IN_GROUP;

    return (
      <div key={designation} className={styles.designationGroup}>
        <div 
          className={styles.designationHeader}
          style={{ borderLeftColor: designationInfo.color }}
        >
          <span className={styles.designationIcon}>{designationInfo.icon}</span>
          <span className={styles.designationName}>{designation}</span>
          <span className={styles.designationCount}>({employeeList.length})</span>
        </div>
        
        <div className={styles.designationEmployees}>
          {visibleEmployees.map(({ employee }, index) => 
            renderEmployeeImage(employee, index, designation)
          )}
          
          {remainingCount > 0 && (
            <div 
              className={styles.moreEmployeesIndicator}
              style={{ backgroundColor: designationInfo.color }}
            >
              +{remainingCount}
            </div>
          )}
        </div>
      </div>
    );
  }, [renderEmployeeImage]);

  const renderGeneralEmployees = useCallback(() => {
    if (groupedData.generalEmployees.length === 0) return null;

    const maxVisible = groupedData.hasSpecialGroups 
      ? GROUPING_CONFIG.MAX_VISIBLE_GENERAL_WITH_GROUPS 
      : GROUPING_CONFIG.MAX_VISIBLE_GENERAL;
    
    const visibleEmployees = groupedData.generalEmployees.slice(0, maxVisible);
    const remainingCount = groupedData.generalEmployees.length - maxVisible;

    return (
      <div className={styles.generalEmployeesSection}>
        {groupedData.hasSpecialGroups && (
          <div className={styles.generalHeader}>
            <span className={styles.generalIcon}>👥</span>
            <span className={styles.generalLabel}>Other Employees</span>
            <span className={styles.generalCount}>({groupedData.generalEmployees.length})</span>
          </div>
        )}
        
        <div className={styles.generalEmployeesRow}>
          {visibleEmployees.map(({ employee }, index) => 
            renderEmployeeImage(employee, index)
          )}
          
          {remainingCount > 0 && (
            <div className={styles.moreEmployeesIndicator}>
              +{remainingCount}
            </div>
          )}
        </div>
      </div>
    );
  }, [groupedData, renderEmployeeImage]);

  // ============================================================================
  // RENDER CONDITION
  // ============================================================================

  if (validEmployees.length === 0) return null;

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <>
      {/* Backdrop */}
      <div className={styles.backdrop} onClick={onClose} />
      
      {/* Enhanced Gallery Card with Consistent Grouping */}
      <div className={`${styles.imageGalleryCard} ${groupedData.hasSpecialGroups ? styles.hasSpecialGroups : ''}`}>
        {/* Header with Total Count and Grouping Info */}
        <div className={styles.galleryHeader}>
          <div className={styles.employeeCount}>
            {groupedData.totalEmployees} Employee{groupedData.totalEmployees !== 1 ? 's' : ''}
          </div>
          {groupedData.hasSpecialGroups && (
            <div className={styles.groupingIndicator}>
              <span className={styles.groupIcon}>🏗️</span>
              <span>
                {groupedData.groupingSummary.specialGroupCount} Special Group{groupedData.groupingSummary.specialGroupCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Employee Groups Container */}
        <div className={styles.employeeGroupsContainer}>
          {/* Render Special Designation Groups (Sorted by Priority) */}
          {Object.entries(groupedData.specialGroups).map(([designation, employeeList]) => 
            renderDesignationGroup(designation, employeeList)
          )}
          
          {/* Render General Employees */}
          {renderGeneralEmployees()}
        </div>

        {/* Close Button */}
        <button className={styles.closeButton} onClick={onClose}>
          ×
        </button>
      </div>
    </>
  );
};

// ============================================================================
// EXPORT WITH DISPLAY NAME
// ============================================================================

EmployeeImageGallery.displayName = 'EmployeeImageGallery';

export default React.memo(EmployeeImageGallery);